// Edge Expander for infragraph
// Expands edges based on scheme (one2one, many2many, ring) and slice notation

import {
  InfrastructureEdge,
  DeviceEdge,
  ExpandedEdge,
  EdgeScheme,
  ExpandedComponent
} from '../types/infragraph';
import { SliceNotationParser } from './sliceNotation';

export class EdgeExpander {
  private sliceParser: SliceNotationParser;

  constructor() {
    this.sliceParser = new SliceNotationParser();
  }

  /**
   * Expand infrastructure edge based on scheme
   *
   * @param edge - The infrastructure edge to expand
   * @param instanceComponents - Map of instance components for count lookup
   * @returns Array of expanded edges
   */
  expandInfrastructureEdge(
    edge: InfrastructureEdge,
    instanceComponents?: Map<string, ExpandedComponent[]>
  ): ExpandedEdge[] {
    // Parse both endpoints with slice notation
    const ep1Instances = this.parseEndpoint(edge.ep1.instance);
    const ep1Components = this.parseEndpoint(edge.ep1.component);

    const ep2Instances = this.parseEndpoint(edge.ep2.instance);
    const ep2Components = this.parseEndpoint(edge.ep2.component);

    // Parse optional device field for sub-component connections
    // Format: "TPU_V4_CHIP.ici_port[0]" → extract component path after first dot
    const ep1Devices = edge.ep1.device ? this.parseDeviceField(edge.ep1.device) : null;
    const ep2Devices = edge.ep2.device ? this.parseDeviceField(edge.ep2.device) : null;

    // Generate all source and target combinations based on scheme
    const edges: ExpandedEdge[] = [];

    switch (edge.scheme) {
      case 'one2one':
        edges.push(...this.expandOne2One(
          ep1Instances,
          ep1Components,
          ep2Instances,
          ep2Components,
          edge.link,
          edge.scheme,
          ep1Devices,
          ep2Devices
        ));
        break;

      case 'many2many':
        edges.push(...this.expandMany2Many(
          ep1Instances,
          ep1Components,
          ep2Instances,
          ep2Components,
          edge.link,
          edge.scheme,
          ep1Devices,
          ep2Devices
        ));
        break;

      case 'ring':
        edges.push(...this.expandRing(
          ep1Instances,
          ep1Components,
          ep2Instances,
          ep2Components,
          edge.link,
          edge.scheme,
          ep1Devices,
          ep2Devices
        ));
        break;

      default:
        throw new Error(`Unknown edge scheme: ${edge.scheme}`);
    }

    return edges;
  }

  /**
   * Expand device edge (internal to a device)
   *
   * @param edge - The device edge to expand
   * @param deviceInstanceName - The device instance name (e.g., "server.0")
   * @returns Array of expanded edges
   */
  expandDeviceEdge(edge: DeviceEdge, deviceInstanceName: string): ExpandedEdge[] {
    // Parse both endpoints with slice notation
    const ep1Components = this.parseEndpoint(edge.ep1.component);
    const ep2Components = this.parseEndpoint(edge.ep2.component);

    // For device edges, we might have a device prefix or not
    const ep1Device = edge.ep1.device || '';
    const ep2Device = edge.ep2.device || '';

    // Build full node names
    // If device field is specified (device-in-device edges), parse and expand it
    let ep1FullNames: string[];
    let ep2FullNames: string[];

    if (ep1Device) {
      // Two cases for device field:
      // 1. Device-in-device component reference: "TPU_V4_CHIP.ici_port[0]"
      //    Format: <DEVICE_TYPE>.<component-path>
      //    Extract component path after first dot and combine with deviceInstanceName
      // 2. Full instance path (legacy): "server.0"
      //    Use as complete replacement for deviceInstanceName
      //
      // Heuristic: if device field contains uppercase letter followed by dot and
      // the part after dot looks like a component name (contains letters or brackets),
      // treat as device-in-device. Otherwise treat as instance path.
      const dotIndex = ep1Device.indexOf('.');
      const afterDot = dotIndex > 0 ? ep1Device.substring(dotIndex + 1) : '';
      const isDeviceInDevice = dotIndex > 0 && /[a-zA-Z[]/.test(afterDot);

      if (isDeviceInDevice) {
        // Device-in-device: extract component path after first dot
        const subComponentPath = ep1Device.substring(dotIndex + 1);
        const ep1DevicePaths = this.parseEndpoint(subComponentPath);
        ep1FullNames = ep1Components.flatMap(comp =>
          ep1DevicePaths.map(devPath => `${deviceInstanceName}.${comp}.${devPath}`)
        );
      } else {
        // Full instance path: use device field directly, ignore deviceInstanceName
        ep1FullNames = ep1Components.map(comp => `${ep1Device}.${comp}`);
      }
    } else {
      // Normal case: just prefix with device instance name
      ep1FullNames = ep1Components.map(comp => `${deviceInstanceName}.${comp}`);
    }

    if (ep2Device) {
      // Same logic as ep1Device - detect device-in-device vs instance path
      const dotIndex = ep2Device.indexOf('.');
      const afterDot = dotIndex > 0 ? ep2Device.substring(dotIndex + 1) : '';
      const isDeviceInDevice = dotIndex > 0 && /[a-zA-Z[]/.test(afterDot);

      if (isDeviceInDevice) {
        // Device-in-device: extract component path after first dot
        const subComponentPath = ep2Device.substring(dotIndex + 1);
        const ep2DevicePaths = this.parseEndpoint(subComponentPath);
        ep2FullNames = ep2Components.flatMap(comp =>
          ep2DevicePaths.map(devPath => `${deviceInstanceName}.${comp}.${devPath}`)
        );
      } else {
        // Full instance path: use device field directly, ignore deviceInstanceName
        ep2FullNames = ep2Components.map(comp => `${ep2Device}.${comp}`);
      }
    } else {
      // Normal case: just prefix with device instance name
      ep2FullNames = ep2Components.map(comp => `${deviceInstanceName}.${comp}`);
    }

    // Generate edges based on scheme
    const edges: ExpandedEdge[] = [];

    switch (edge.scheme) {
      case 'one2one':
        edges.push(...this.expandOne2OneSimple(ep1FullNames, ep2FullNames, edge.link, edge.scheme));
        break;

      case 'many2many':
        edges.push(...this.expandMany2ManySimple(ep1FullNames, ep2FullNames, edge.link, edge.scheme));
        break;

      case 'ring':
        edges.push(...this.expandRingSimple(ep1FullNames, ep2FullNames, edge.link, edge.scheme));
        break;

      default:
        throw new Error(`Unknown edge scheme: ${edge.scheme}`);
    }

    return edges;
  }

  /**
   * Parse device field to extract component path
   * Format: "TPU_V4_CHIP.ici_port[0]" → "ici_port[0]"
   *
   * @param deviceField - Device field from infrastructure endpoint
   * @returns Array of expanded component paths
   */
  private parseDeviceField(deviceField: string): string[] {
    // Extract component path after first dot
    const dotIndex = deviceField.indexOf('.');
    if (dotIndex < 0) {
      // No dot found, return as-is (shouldn't happen in valid data)
      return this.parseEndpoint(deviceField);
    }

    const componentPath = deviceField.substring(dotIndex + 1);
    return this.parseEndpoint(componentPath);
  }

  /**
   * Parse endpoint with slice notation
   * Handles device-in-device notation (e.g., "CX6[0:8].port[1]")
   *
   * @param endpoint - Endpoint string with possible slice notation
   * @returns Array of expanded names
   */
  private parseEndpoint(endpoint: string): string[] {
    try {
      // Check for device-in-device notation (contains dot)
      const dotIndex = endpoint.indexOf('.');

      if (dotIndex > 0) {
        // Device-in-device: expand both parts and combine
        // Example: "CX6[0:8].port[1]" → ["CX6.0.port.1", "CX6.1.port.1", ...]
        const devicePart = endpoint.substring(0, dotIndex);
        const componentPart = endpoint.substring(dotIndex + 1);

        // Expand device part (if it has brackets)
        const deviceNames = this.sliceParser.hasSliceSyntax(devicePart)
          ? this.sliceParser.expandToNames(devicePart)
          : [devicePart];

        // Expand component part (if it has brackets)
        const componentNames = this.sliceParser.hasSliceSyntax(componentPart)
          ? this.sliceParser.expandToNames(componentPart)
          : [componentPart];

        // Combine: create cartesian product
        const result: string[] = [];
        for (const device of deviceNames) {
          for (const component of componentNames) {
            result.push(`${device}.${component}`);
          }
        }

        return result;
      } else {
        // Regular notation without dot
        return this.sliceParser.expandToNames(endpoint);
      }
    } catch (error) {
      // If parsing fails, treat as a single literal name
      return [endpoint];
    }
  }

  /**
   * Expand one2one scheme: ep1[i] connects to ep2[i]
   */
  private expandOne2One(
    ep1Instances: string[],
    ep1Components: string[],
    ep2Instances: string[],
    ep2Components: string[],
    link: string,
    scheme: EdgeScheme,
    ep1Devices: string[] | null = null,
    ep2Devices: string[] | null = null
  ): ExpandedEdge[] {
    const edges: ExpandedEdge[] = [];

    // Flatten each side into full node names
    // If device field is present, add it as a third level
    const ep1Nodes = ep1Instances.flatMap(inst =>
      ep1Components.flatMap(comp =>
        ep1Devices ? ep1Devices.map(dev => `${inst}.${comp}.${dev}`) : [`${inst}.${comp}`]
      )
    );

    const ep2Nodes = ep2Instances.flatMap(inst =>
      ep2Components.flatMap(comp =>
        ep2Devices ? ep2Devices.map(dev => `${inst}.${comp}.${dev}`) : [`${inst}.${comp}`]
      )
    );

    // one2one pairs them up sequentially
    const maxLen = Math.min(ep1Nodes.length, ep2Nodes.length);

    for (let i = 0; i < maxLen; i++) {
      edges.push({
        source: ep1Nodes[i],
        target: ep2Nodes[i],
        link,
        scheme
      });
    }

    return edges;
  }

  /**
   * Expand many2many scheme: full mesh between all combinations
   */
  private expandMany2Many(
    ep1Instances: string[],
    ep1Components: string[],
    ep2Instances: string[],
    ep2Components: string[],
    link: string,
    scheme: EdgeScheme,
    ep1Devices: string[] | null = null,
    ep2Devices: string[] | null = null
  ): ExpandedEdge[] {
    const edges: ExpandedEdge[] = [];

    for (const inst1 of ep1Instances) {
      for (const comp1 of ep1Components) {
        const ep1DeviceList = ep1Devices || [''];
        for (const dev1 of ep1DeviceList) {
          for (const inst2 of ep2Instances) {
            for (const comp2 of ep2Components) {
              const ep2DeviceList = ep2Devices || [''];
              for (const dev2 of ep2DeviceList) {
                const source = dev1 ? `${inst1}.${comp1}.${dev1}` : `${inst1}.${comp1}`;
                const target = dev2 ? `${inst2}.${comp2}.${dev2}` : `${inst2}.${comp2}`;

                // Avoid self-loops
                if (source !== target) {
                  edges.push({
                    source,
                    target,
                    link,
                    scheme
                  });
                }
              }
            }
          }
        }
      }
    }

    return edges;
  }

  /**
   * Expand ring scheme: circular topology ep1[i] → ep2[i], ep2[i] → ep1[i+1]
   */
  private expandRing(
    ep1Instances: string[],
    ep1Components: string[],
    ep2Instances: string[],
    ep2Components: string[],
    link: string,
    scheme: EdgeScheme,
    ep1Devices: string[] | null = null,
    ep2Devices: string[] | null = null
  ): ExpandedEdge[] {
    const edges: ExpandedEdge[] = [];

    const ep1Nodes = ep1Instances.flatMap(inst =>
      ep1Components.flatMap(comp =>
        ep1Devices ? ep1Devices.map(dev => `${inst}.${comp}.${dev}`) : [`${inst}.${comp}`]
      )
    );

    const ep2Nodes = ep2Instances.flatMap(inst =>
      ep2Components.flatMap(comp =>
        ep2Devices ? ep2Devices.map(dev => `${inst}.${comp}.${dev}`) : [`${inst}.${comp}`]
      )
    );

    // For ring, we need equal number of nodes on both sides
    const len = Math.min(ep1Nodes.length, ep2Nodes.length);

    for (let i = 0; i < len; i++) {
      const nextIndex = (i + 1) % len;
      edges.push({
        source: ep1Nodes[i],
        target: ep2Nodes[i],
        link,
        scheme
      });
      edges.push({
        source: ep2Nodes[i],
        target: ep1Nodes[nextIndex],
        link,
        scheme
      });
    }

    return edges;
  }

  /**
   * Simplified one2one for already-expanded endpoint arrays
   */
  private expandOne2OneSimple(
    sources: string[],
    targets: string[],
    link: string,
    scheme: EdgeScheme
  ): ExpandedEdge[] {
    const edges: ExpandedEdge[] = [];
    const maxLen = Math.min(sources.length, targets.length);

    for (let i = 0; i < maxLen; i++) {
      edges.push({
        source: sources[i],
        target: targets[i],
        link,
        scheme
      });
    }

    return edges;
  }

  /**
   * Simplified many2many for already-expanded endpoint arrays
   */
  private expandMany2ManySimple(
    sources: string[],
    targets: string[],
    link: string,
    scheme: EdgeScheme
  ): ExpandedEdge[] {
    const edges: ExpandedEdge[] = [];

    for (const source of sources) {
      for (const target of targets) {
        if (source !== target) {
          edges.push({
            source,
            target,
            link,
            scheme
          });
        }
      }
    }

    return edges;
  }

  /**
   * Simplified ring for already-expanded endpoint arrays
   */
  private expandRingSimple(
    sources: string[],
    targets: string[],
    link: string,
    scheme: EdgeScheme
  ): ExpandedEdge[] {
    const edges: ExpandedEdge[] = [];
    const len = Math.min(sources.length, targets.length);

    for (let i = 0; i < len; i++) {
      const nextIndex = (i + 1) % len;
      edges.push({
        source: sources[i],
        target: targets[i],
        link,
        scheme
      });
      edges.push({
        source: targets[i],
        target: sources[nextIndex],
        link,
        scheme
      });
    }

    return edges;
  }

  /**
   * Get statistics about edge expansion
   *
   * @param edges - Array of infrastructure edges
   * @returns Statistics object
   */
  getExpansionStats(edges: InfrastructureEdge[]): {
    totalEdges: number;
    byScheme: Record<EdgeScheme, number>;
  } {
    const stats = {
      totalEdges: edges.length,
      byScheme: {
        one2one: 0,
        many2many: 0,
        ring: 0
      } as Record<EdgeScheme, number>
    };

    for (const edge of edges) {
      stats.byScheme[edge.scheme]++;
    }

    return stats;
  }
}

// Export singleton instance for convenience
export const edgeExpander = new EdgeExpander();

// Export helper functions for direct use
export function expandInfrastructureEdge(
  edge: InfrastructureEdge,
  instanceComponents?: Map<string, ExpandedComponent[]>
): ExpandedEdge[] {
  return edgeExpander.expandInfrastructureEdge(edge, instanceComponents);
}

export function expandDeviceEdge(edge: DeviceEdge, deviceInstanceName: string): ExpandedEdge[] {
  return edgeExpander.expandDeviceEdge(edge, deviceInstanceName);
}
